import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { jotaiStore } from '@/atoms/store';
import { projectIdAtom } from '@/atoms/workspace';
import type { Import, SseJobEvent } from '@/types';

export const IMPORT_MODAL_DEFAULTS: Import = {
    open: false,
    showRules: false,
    showStatistics: false,
    showCharacters: false,
    loader: false,
    statistics: null,
    checked: false,
    ready: false,
    characters_union: null,
};

export const importModalAtom = atom<Import>(IMPORT_MODAL_DEFAULTS);

// True while a document import is streaming over SSE for this browser tab.
// Only the project actually being rewritten should go read-only (see
// `isImportLockingProject`); this flag alone is used to disable starting a
// *second* import (on any project) — `activeImportMarkerAtom` tracks a single
// import at a time, so a second one would clobber the marker this tab is
// following rather than run alongside it.
export const importInProgressAtom = atom(false);

// Persisted marker of an in-flight import, so a page refresh can re-attach to
// the SSE stream (the backend keeps processing regardless of the client).
// Cleared when the stream ends; `started_at` lets stale markers expire.
// `last_event` is updated on every received event — the backend cannot replay
// missed events, so it's the freshest state available right after a refresh.
// `last_event_at` anchors the resume idle window: silence is measured from the
// last seen event, so time spent with the page closed counts toward it.
export const activeImportMarkerAtom = atomWithStorage<{
    project_id: number;
    started_at: number;
    last_event?: SseJobEvent | null;
    last_event_at?: number;
} | null>('active_import', null, undefined, {
    getOnInit: true,
});

// The backend now tags every pushed SSE message with the importing
// `project_id` (previously the Redis channel was global with no way to tell
// projects apart), so the read-only lock can be scoped to that project
// instead of blanket-locking whatever project the user happens to be
// viewing. Reads `projectIdAtom` imperatively so it works outside React too
// (MutationCache callbacks, the modals gate).
export const isImportLockingProject = () => {
    const marker = jotaiStore.get(activeImportMarkerAtom);
    return marker !== null && marker.project_id === jotaiStore.get(projectIdAtom);
};
